import {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';
import {
  interpolate,
  Extrapolation,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
  withDelay,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { BottomSheet } from '@bonanza-social/navigation';
import { Box, Image, PressableOverlay, Text } from '@bonanza-social/moonlight';
import { PrizeWallPrizeProps } from './PrizeWallPrize.types';
import PrizeWallPrizeBackground from './PrizeWallPrizeBackground/PrizeWallPrizeBackground';
import PrizeWallPrizeQuantity from './PrizeWallPrizeQuantity/PrizeWallPrizeQuantity';

function PrizeWallPrize(
  {
    name,
    imageUrl,
    position,
    awarded,
    outOfStock,
    description,
    totalQuantity,
    remainingQuantity,
    quantityLocked,
    quantityFormat,
    animatedValue,
  }: PrizeWallPrizeProps,
  ref: ForwardedRef<View>,
) {
  const mounted = useRef(false);
  const quantityAnimatedValue = useSharedValue(0);
  const quantityAnimatedStyle = useAnimatedStyle(() => ({
    opacity: quantityAnimatedValue.value,
  }));
  const backgroundAnimatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(
      animatedValue.value - position,
      [0, 0.5, 1],
      [0, 1, 0],
      Extrapolation.CLAMP,
    ),
  }));
  const [localRemainingQuantity, setLocalRemainingQuantity] =
    useState(remainingQuantity);

  const onPress = useCallback(() => {
    BottomSheet.open({
      namespace: 'prizeWall',
      type: 'PrizeWallPrizeInfo',
      title: name,
      data: {
        imageUrl,
        description,
      },
    });
  }, [name, imageUrl, description]);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;

      return;
    }

    if (quantityLocked) {
      return;
    }

    const updateLocalRemainingQuantity = () =>
      setLocalRemainingQuantity(remainingQuantity);

    quantityAnimatedValue.value = withSequence(
      withTiming(0.5, { duration: 150 }, () => {
        scheduleOnRN(updateLocalRemainingQuantity);
      }),
      withTiming(1.0, { duration: 150 }),
      withDelay(1000, withTiming(0, { duration: 500 })),
    );
  }, [quantityLocked, remainingQuantity, quantityAnimatedValue]);

  return (
    <Box ref={ref} flex={1} aspectRatio={1} maxHeight="100%">
      <PrizeWallPrizeBackground
        variant={!localRemainingQuantity && outOfStock ? 'disabled' : 'default'}
      />
      <PrizeWallPrizeBackground
        variant={awarded ? 'awarded' : 'focused'}
        animatedStyle={backgroundAnimatedStyle}
      />
      <Box height="100%" gap="space-200" padding="space-200">
        <Box>
          <PrizeWallPrizeQuantity
            light={false}
            totalQuantity={totalQuantity}
            remainingQuantity={localRemainingQuantity}
            quantityFormat={quantityFormat}
          />
          <PrizeWallPrizeQuantity
            light={true}
            totalQuantity={totalQuantity}
            remainingQuantity={localRemainingQuantity}
            quantityFormat={quantityFormat}
            animatedStyle={quantityAnimatedStyle}
            position="absolute"
            top="space-0"
            left="space-0"
          />
        </Box>
        <Box
          flex={1}
          flexShrink={1}
          paddingHorizontal="space-500"
          centerContent
        >
          <Image
            source={imageUrl}
            width="100%"
            maxHeight="100%"
            aspectRatio={1}
          />
        </Box>
        {outOfStock && (
          <Box position="absolute" inset={0} zIndex={2} centerContent>
            <Text
              variant="body-s-semi"
              locNS="prizeWall"
              locKey="prizeWallPrizeOutOfStock"
              color="darkSurfaceSecondary"
              paddingHorizontal="space-300"
              textAlign="center"
            />
          </Box>
        )}
        <Text
          variant="badge"
          paddingBottom="space-025"
          color="darkSurfacePrimary"
          textAlign="center"
        >
          {name}
        </Text>
      </Box>
      <PressableOverlay onPress={onPress} />
    </Box>
  );
}

export default memo(forwardRef(PrizeWallPrize));
